import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Icon } from "semantic-ui-react";
import moment from "moment";
import "moment/min/locales.min";
moment.locale("sk");

const ACCENT = "#e03997";
const ACCENT_SOFT = "rgba(224, 57, 151, 0.12)";

const fieldStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    padding: "0.9rem 1rem",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    cursor: "pointer",
    outline: "none",
};

const labelMini = { fontSize: "0.7rem", color: "#999", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" };
const valueText = { fontSize: "0.95rem", color: "#333", fontWeight: "bold" };

function MobileDateTimePicker({ date, time, onDateChange, onTimeChange, minDate, maxDate }) {
    const [panel, setPanel] = useState(null);
    const [shown, setShown] = useState(false);
    const [viewMonth, setViewMonth] = useState(moment(date || undefined).startOf("month"));
    const [tempDate, setTempDate] = useState(date || null);
    const [tempTime, setTempTime] = useState(time || null);
    const [dragY, setDragY] = useState(0);
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef(0);
    const timeListRef = useRef(null);

    const min = minDate ? moment(minDate, "YYYY-MM-DD") : null;
    const max = maxDate ? moment(maxDate, "YYYY-MM-DD") : null;

    const open = (p) => {
        if (p === "date") {
            setViewMonth(moment(date || undefined).startOf("month"));
            setTempDate(date || null);
        }
        if (p === "time") setTempTime(time || null);
        setPanel(p);
        requestAnimationFrame(() => setShown(true));
    };

    const close = () => {
        setShown(false);
        setTimeout(() => setPanel(null), 220);
    };

    const confirm = () => {
        if (panel === "date" && tempDate) onDateChange(tempDate);
        if (panel === "time" && tempTime) onTimeChange(tempTime);
        close();
    };

    const onDragStart = (e) => {
        dragStart.current = e.touches ? e.touches[0].clientY : e.clientY;
        setDragging(true);
        if (e.pointerId !== undefined && e.currentTarget.setPointerCapture) {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
        }
    };

    const onDragMove = (e) => {
        if (!dragging) return;
        const y = e.touches ? e.touches[0].clientY : e.clientY;
        const delta = y - dragStart.current;
        if (delta > 0) setDragY(delta);
    };

    const onDragEnd = () => {
        if (!dragging) return;
        setDragging(false);
        if (dragY > 110) close();
        setDragY(0);
    };

    useEffect(() => {
        if (panel === "time" && timeListRef.current) {
            const active = timeListRef.current.querySelector("[data-active='true']");
            if (active) active.scrollIntoView({ block: "center" });
        }
    }, [panel]);

    const days = (() => {
        const start = viewMonth.clone().startOf("month").startOf("isoWeek");
        const end = viewMonth.clone().endOf("month").endOf("isoWeek");
        const arr = [];
        const d = start.clone();
        while (d.isSameOrBefore(end, "day")) {
            arr.push(d.clone());
            d.add(1, "day");
        }
        return arr;
    })();

    const isDisabled = (d) => (min && d.isBefore(min, "day")) || (max && d.isAfter(max, "day"));
    const canPrev = !min || viewMonth.clone().subtract(1, "month").endOf("month").isSameOrAfter(min, "day");
    const canNext = !max || viewMonth.clone().add(1, "month").startOf("month").isSameOrBefore(max, "day");

    const selectDay = (d) => {
        if (isDisabled(d)) return;
        setTempDate(d.format("YYYY-MM-DD"));
    };

    const times = (() => {
        const arr = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                arr.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
            }
        }
        return arr;
    })();

    const weekdays = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
    const selectedDate = tempDate ? moment(tempDate, "YYYY-MM-DD") : null;
    const today = moment();
    const canConfirm = panel === "date" ? !!tempDate : !!tempTime;

    return (
        <>
            <div style={{ display: "flex", gap: "0.9rem", marginBottom: "0.25rem" }}>
                <button type="button" onClick={() => open("date")} style={fieldStyle}>
                    <Icon name="calendar alternate outline" size="large" style={{ color: ACCENT, margin: 0 }} />
                    <div style={{ textAlign: "left", flex: 1 }}>
                        <div style={labelMini}>Dátum</div>
                        <div style={valueText}>{date ? moment(date).format("dd D. MMM") : "Vyber"}</div>
                    </div>
                    <Icon name="chevron down" style={{ color: "#ccc", margin: 0 }} />
                </button>
                <button type="button" onClick={() => open("time")} style={fieldStyle}>
                    <Icon name="clock outline" size="large" style={{ color: ACCENT, margin: 0 }} />
                    <div style={{ textAlign: "left", flex: 1 }}>
                        <div style={labelMini}>Čas</div>
                        <div style={valueText}>{time || "Vyber"}</div>
                    </div>
                    <Icon name="chevron down" style={{ color: "#ccc", margin: 0 }} />
                </button>
            </div>

            {panel && createPortal(
                <div
                    onClick={close}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0,0,0,0.45)",
                        zIndex: 100001,
                        opacity: shown ? 1 : 0,
                        transition: "opacity 0.22s ease",
                        display: "flex",
                        alignItems: "flex-end",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: "100%",
                            background: "#fff",
                            borderTopLeftRadius: "20px",
                            borderTopRightRadius: "20px",
                            boxShadow: "0 -6px 30px rgba(0,0,0,0.25)",
                            transform: !shown ? "translateY(100%)" : `translateY(${dragY}px)`,
                            transition: dragging ? "none" : "transform 0.22s ease",
                            maxHeight: "88vh",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            onPointerDown={onDragStart}
                            onPointerMove={onDragMove}
                            onPointerUp={onDragEnd}
                            onPointerCancel={onDragEnd}
                            style={{ flexShrink: 0, cursor: "grab", touchAction: "none", paddingTop: "0.8rem" }}
                        >
                            <div style={{ width: "44px", height: "5px", background: "#e0e0e0", borderRadius: "3px", margin: "0 auto 0.8rem" }} />

                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.1rem 1rem" }}>
                                <span style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#222" }}>
                                    {panel === "date" ? "Vyber dátum" : "Vyber čas"}
                                </span>
                                <button
                                    type="button"
                                    onPointerDown={(e) => { e.stopPropagation(); close(); }}
                                    style={{ width: "40px", height: "40px", flexShrink: 0, borderRadius: "50%", border: "none", background: "#f4f4f6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                >
                                    <Icon name="close" style={{ margin: 0, color: "#555" }} />
                                </button>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "0 1.1rem" }}>
                        {panel === "date" ? (
                            <div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.9rem" }}>
                                    <button
                                        type="button"
                                        disabled={!canPrev}
                                        onClick={() => canPrev && setViewMonth(viewMonth.clone().subtract(1, "month"))}
                                        style={{ background: "#f4f4f6", border: "none", borderRadius: "50%", width: "38px", height: "38px", cursor: canPrev ? "pointer" : "default", opacity: canPrev ? 1 : 0.4 }}
                                    >
                                        <Icon name="chevron left" style={{ margin: 0, color: "#555" }} />
                                    </button>
                                    <span style={{ fontSize: "1rem", fontWeight: "bold", color: "#333", textTransform: "capitalize" }}>
                                        {viewMonth.format("MMMM YYYY")}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={!canNext}
                                        onClick={() => canNext && setViewMonth(viewMonth.clone().add(1, "month"))}
                                        style={{ background: "#f4f4f6", border: "none", borderRadius: "50%", width: "38px", height: "38px", cursor: canNext ? "pointer" : "default", opacity: canNext ? 1 : 0.4 }}
                                    >
                                        <Icon name="chevron right" style={{ margin: 0, color: "#555" }} />
                                    </button>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: "0.4rem" }}>
                                    {weekdays.map((w) => (
                                        <div key={w} style={{ textAlign: "center", fontSize: "0.75rem", fontWeight: "bold", color: "#aaa", padding: "0.3rem 0" }}>{w}</div>
                                    ))}
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
                                    {days.map((d) => {
                                        const inMonth = d.month() === viewMonth.month();
                                        const disabled = isDisabled(d);
                                        const selected = selectedDate && d.isSame(selectedDate, "day");
                                        const isToday = d.isSame(today, "day");
                                        return (
                                            <button
                                                key={d.format("YYYY-MM-DD")}
                                                type="button"
                                                onClick={() => selectDay(d)}
                                                disabled={disabled}
                                                style={{
                                                    aspectRatio: "1 / 1",
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    background: selected ? ACCENT : "transparent",
                                                    color: selected ? "#fff" : disabled ? "#d5d5d5" : inMonth ? "#333" : "#c2c2c2",
                                                    fontWeight: selected || isToday ? "bold" : "normal",
                                                    fontSize: "0.95rem",
                                                    cursor: disabled ? "default" : "pointer",
                                                    boxShadow: isToday && !selected ? `inset 0 0 0 2px ${ACCENT}` : "none",
                                                }}
                                            >
                                                {d.date()}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div ref={timeListRef} style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", paddingTop: "0.2rem" }}>
                                {times.map((t) => {
                                    const active = t === tempTime;
                                    return (
                                        <button
                                            key={t}
                                            type="button"
                                            data-active={active}
                                            onClick={() => setTempTime(t)}
                                            style={{
                                                padding: "0.7rem 0",
                                                border: active ? `1px solid ${ACCENT}` : "1px solid #ececec",
                                                borderRadius: "10px",
                                                background: active ? ACCENT_SOFT : "#fafafa",
                                                color: active ? ACCENT : "#444",
                                                fontWeight: active ? "bold" : "normal",
                                                fontSize: "0.95rem",
                                                cursor: "pointer",
                                            }}
                                        >
                                            {t}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        </div>

                        <div style={{ padding: "0.9rem 1.1rem calc(1.1rem + env(safe-area-inset-bottom))", borderTop: "1px solid #eee", flexShrink: 0 }}>
                            <button
                                type="button"
                                onClick={confirm}
                                disabled={!canConfirm}
                                style={{
                                    width: "100%",
                                    padding: "1rem",
                                    border: "none",
                                    borderRadius: "12px",
                                    background: canConfirm ? ACCENT : "#f0bcd9",
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    cursor: canConfirm ? "pointer" : "default",
                                }}
                            >
                                Potvrdiť
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

export default MobileDateTimePicker;
